import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { APP_VERSION } from '../version';

const PLAN_LABELS = { enterprise: 'Enterprise', pro: 'Pro' };

export default function AccountMenu() {
  const { user, business, parentCompany, isOwner, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close on navigation and on taps outside the popover
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const initials = (user?.name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase() || '?';
  const plan = PLAN_LABELS[parentCompany?.plan] || 'Starter';

  // Logging out clears this phone's copy, including anything still waiting to
  // sync — so warn first if some records haven't reached the server.
  const handleLogout = async () => {
    const { pending } = await logout();
    if (pending > 0 && window.confirm(
      `${pending} record${pending === 1 ? " hasn't" : "s haven't"} reached the server yet (no connection). ` +
      'Logging out now will delete them from this phone. Log out anyway?'
    )) {
      await logout({ force: true });
    }
  };

  return (
    <div className="sidebar-account" ref={ref}>
      <button
        className="sidebar-avatar"
        onClick={() => setOpen(o => !o)}
        title="Account"
        aria-label="Account"
        aria-expanded={open}
      >
        {initials}
      </button>
      {/* Always visible, so users can say which version they are on. */}
      <div className="sidebar-version" title={`ShopBook version ${APP_VERSION}`}>v{APP_VERSION}</div>

      {open && (
        <div className="account-popover" role="menu">
          <div className="account-popover-biz">
            {business?.name}
            <span>{plan}{business?.currency ? ` · ${business.currency}` : ''}</span>
          </div>
          {isOwner && (
            <button className="account-popover-item" role="menuitem" onClick={() => navigate('/team')}>
              <span>🤝</span> Team
            </button>
          )}
          {isOwner && (
            <button className="account-popover-item" role="menuitem" onClick={() => navigate('/settings')}>
              <span>⚙️</span> Settings
            </button>
          )}
          <button className="account-popover-item account-popover-logout" role="menuitem" onClick={handleLogout}>
            <span>🚪</span> Log out
          </button>
          <div className="account-popover-version">ShopBook v{APP_VERSION}</div>
        </div>
      )}
    </div>
  );
}
